import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../config/axios';
import CustomerLayout from '../../components/Layout/CustomerLayout';
import CustomerCreateSupportCaseModal from '../../components/SupportCase/CustomerCreateSupportCaseModal';
import { FaPlus, FaSearch, FaFilter } from 'react-icons/fa';
import { FaFilter as FaFilterIcon } from "react-icons/fa6";
import { MdClose } from 'react-icons/md';
import { useLanguage } from '../../context/LanguageContext';

const CustomerSupportCasesPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [supportCases, setSupportCases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  // Sayfalama için state'ler
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  // Arama için state
  const [searchTerm, setSearchTerm] = useState('');
  // Modal için state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Column filters
  const [filters, setFilters] = useState({
    case_id: '',
    subject: '',
    case_status: '',
    created_at: '',
    assigned_to_name: ''
  });
  // Popup state
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filterPopupMessage, setFilterPopupMessage] = useState('');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchSupportCases = async () => {
      if (!user || !user.customer_id) {
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/support-cases', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.length > 0) {
            
            // Let's check various possible customer ID field names
            const firstCase = response.data[0];
        }

        // Try multiple possible customer ID field names for filtering
        const customerCases = response.data.filter(caseItem => {
          // Check multiple possible field names
          const possibleMatches = [
            String(caseItem.customer_id) === String(user.customer_id),
            String(caseItem.company_customer_id) === String(user.customer_id),
            String(caseItem.customer) === String(user.customer_id),
            String(caseItem.company_id) === String(user.customer_id),
            // Also check if customer_name matches user's customer_name if available
            user.customer_name && caseItem.customer_name === user.customer_name
          ];
          
          return possibleMatches.some(match => match);
        });

        setSupportCases(customerCases);

      } catch (err) {
        setError(t('messages.supportCaseFetchError'));
        console.error("Error fetching support cases:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSupportCases();
  }, [user]);

  // Arama terimi değiştiğinde ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handle clicks outside filter dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      // Close all filter dropdowns if clicked outside
      Object.keys(filters).forEach(field => {
        const element = document.getElementById(`filter-${field}`);
        if (element && !element.contains(event.target) && 
            !event.target.closest(`button[data-filter="${field}"]`)) {
          element.style.display = 'none';
        }
      });
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filters]);

  // Check for location state to apply filters or open modal
  useEffect(() => {
    if (location.state) {
      const { filter, showPopup, openCreateModal } = location.state;
      
      // Handle filter application
      if (filter && ['OPEN', 'IN PROGRESS', 'CLOSED'].includes(filter)) {
        setFilters(prev => ({
          ...prev,
          case_status: filter
        }));
        
        // Set dynamic popup message based on filter
        const filterMessages = {
          'OPEN': t('customerSupportCases.onlyOpenTickets'),
          'IN PROGRESS': t('customerSupportCases.onlyInProgressTickets'),
          'CLOSED': t('customerSupportCases.onlyClosedTickets')
        };
        setFilterPopupMessage(filterMessages[filter] || '');
        
        if (showPopup) {
          setShowFilterPopup(true);
          // Auto-hide popup after 3 seconds
          setTimeout(() => {
            setShowFilterPopup(false);
          }, 3000);
        }
      }
      
      // Handle opening create modal
      if (openCreateModal) {
        setIsCreateModalOpen(true);
      }
      
      // Clear location state to prevent re-applying filter on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleRowClick = (caseItem) => {
    // UUID varsa UUID kullan, yoksa ID kullan (geriye uyumluluk için)
    const identifier = caseItem.support_case_uuid || caseItem.id;
    navigate(`/customer-support-case/${identifier}`);
  };
  
  const toggleFilter = (field) => {
    // Close all other filters
    Object.keys(filters).forEach(key => {
      if (key !== field) {
        const element = document.getElementById(`filter-${key}`);
        if (element) {
          element.style.display = 'none';
        }
      }
    });
    
    // Toggle the selected filter
    const element = document.getElementById(`filter-${field}`);
    if (element) {
      element.style.display = element.style.display === 'none' ? 'block' : 'none';
    }
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      case_id: '',
      subject: '',
      case_status: '',
      created_at: '',
      assigned_to_name: ''
    });
    setSearchTerm('');
  };
  
  const getStatusChip = (status) => {
    const statusLower = status ? status.toLowerCase() : '';
    if (statusLower.includes('open')) return 'bg-blue-100 text-blue-800';
    if (statusLower.includes('progress')) return 'bg-yellow-100 text-yellow-800';
    if (statusLower.includes('resolved')) return 'bg-green-100 text-green-800';
    if (statusLower.includes('closed')) return 'bg-gray-100 text-gray-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Arama ve filtreleme logic'i
  const filteredCases = supportCases.filter(caseItem => {
    if (!caseItem) return false;
    
    // Global arama terimi filtresi
    const matchesSearchTerm = searchTerm === '' || 
      (caseItem.case_id && caseItem.case_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (caseItem.subject && caseItem.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (caseItem.case_status && caseItem.case_status.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearchTerm) return false;
    
    // Column-specific filters
    const matchesCaseId = filters.case_id === '' || 
      (caseItem.case_id && caseItem.case_id.toLowerCase().includes(filters.case_id.toLowerCase()));
    
    const matchesSubject = filters.subject === '' || 
      (caseItem.subject && caseItem.subject.toLowerCase().includes(filters.subject.toLowerCase()));
    
    const matchesStatus = filters.case_status === '' || 
      (caseItem.case_status && caseItem.case_status.toLowerCase().includes(filters.case_status.toLowerCase()));
    
    const matchesDate = filters.created_at === '' || 
      (caseItem.created_at && new Date(caseItem.created_at).toLocaleString().includes(filters.created_at));
    
    const matchesAssignee = filters.assigned_to_name === '' || 
      ((caseItem.assigned_to_name || 'Atanmadı').toLowerCase().includes(filters.assigned_to_name.toLowerCase()));
    
    return matchesCaseId && matchesSubject && matchesStatus && matchesDate && matchesAssignee;
  });

  // Sayfalama logic'i
  const pageSizeOptions = [10, 20, 50, 100];
  const indexOfLastCase = currentPage * itemsPerPage;
  const indexOfFirstCase = indexOfLastCase - itemsPerPage;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değiştiğinde ilk sayfaya dön
  };

  // Yeni destek talebi oluşturma
  const handleCreateCase = async (newCase) => {
    try {
      setIsLoading(true);
      
      const token = localStorage.getItem('token');
      const response = await api.post('/support-cases', newCase, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsCreateModalOpen(false);
      
      // Destek taleplerini yeniden yükle
      if (user && user.customer_id) {
        const casesResponse = await api.get('/support-cases', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const customerCases = casesResponse.data.filter(caseItem => {
          const possibleMatches = [
            String(caseItem.customer_id) === String(user.customer_id),
            String(caseItem.company_customer_id) === String(user.customer_id),
            String(caseItem.customer) === String(user.customer_id),
            String(caseItem.company_id) === String(user.customer_id),
            user.customer_name && caseItem.customer_name === user.customer_name
          ];
          return possibleMatches.some(match => match);
        });
        
        setSupportCases(customerCases);
      }
      
      alert(t('customerSupportCases.ticketCreatedSuccess'));
    } catch (error) {
      console.error('Error creating customer support case:', error);
      alert(t('customerSupportCases.ticketCreateError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomerLayout>
      <main className="container mx-auto py-8 px-4 dark:text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">{t('customerSupportCases.mySupportTickets')}</h1>
          <div className="flex items-center space-x-2">
            <button 
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <FaPlus className="mr-2" />
              {t('customerSupportCases.createNewTicket')}
            </button>
          </div>
        </div>
        
        {/* Arama Kutusu */}
        <div className="mb-6 relative">
          <input
            type="text"
            placeholder={t('customerSupportCases.searchTickets')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FaSearch className="absolute left-3 top-2.5 text-gray-400" size={20} />
            </div>
        
        {/* Filter reset button */}
        {Object.values(filters).some(filter => filter !== '') && (
          <div className="mb-4">
            <button 
              onClick={clearFilters}
              className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 text-sm flex items-center gap-1"
            >
              <MdClose size={16} />
              {t('common.clear')}
            </button>
          </div>
        )}
        
        {/* Destek Talepleri Tablosu */}
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
          {/* Sayfa boyutu seçici */}
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center rounded-tl-lg rounded-tr-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.show')}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-300">{t('customerSupportCases.records')}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {t('customerSupportCases.totalTickets')}: {filteredCases.length}
              </span>
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <table className="w-full min-w-max table-auto">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center gap-2 relative">
                      {t('customerSupportCases.caseId')}
                      <button 
                        onClick={() => toggleFilter('case_id')}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        title={t('supportCase.filterCaseId')}
                        data-filter="case_id"
                      >
                        <FaFilterIcon size={12} className={filters.case_id ? "text-blue-500" : ""} />
                      </button>
                      <div 
                        id="filter-case_id" 
                        className="absolute mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-md p-2 z-20 border border-gray-200 dark:border-gray-600" 
                        style={{display: 'none'}}
                      >
                        <input
                          type="text"
                          placeholder={t('supportCase.filterCaseIdPlaceholder')}
                          value={filters.case_id}
                          onChange={(e) => handleFilterChange('case_id', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center gap-2 relative">
                      {t('customerSupportCases.subject')}
                      <button 
                        onClick={() => toggleFilter('subject')}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        title={t('supportCase.filterSubject')}
                        data-filter="subject"
                      >
                        <FaFilterIcon size={12} className={filters.subject ? "text-blue-500" : ""} />
                      </button>
                      <div 
                        id="filter-subject" 
                        className="absolute mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-md p-2 z-20 border border-gray-200 dark:border-gray-600" 
                        style={{display: 'none'}}
                      >
                        <input
                          type="text"
                          placeholder={t('supportCase.filterSubjectPlaceholder')}
                          value={filters.subject}
                          onChange={(e) => handleFilterChange('subject', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center gap-2 relative">
                      {t('customerSupportCases.status')}
                      <button 
                        onClick={() => toggleFilter('case_status')}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        title={t('supportCase.filterStatus')}
                        data-filter="case_status"
                      >
                        <FaFilterIcon size={12} className={filters.case_status ? "text-blue-500" : ""} />
                      </button>
                      <div 
                        id="filter-case_status" 
                        className="absolute mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-md p-2 z-20 border border-gray-200 dark:border-gray-600 top-full left-0 w-36" 
                        style={{display: 'none'}}
                      >
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => handleFilterChange('case_status', '')}
                            className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white ${filters.case_status === '' ? 'bg-gray-100 dark:bg-gray-600 font-medium' : ''}`}
                          >
                            {t('customerSupportCases.all')}
                          </button>
                          <button 
                            onClick={() => handleFilterChange('case_status', 'OPEN')}
                            className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white ${filters.case_status === 'OPEN' ? 'bg-gray-100 dark:bg-gray-600 font-medium' : ''}`}
                          >
                            {t('customerSupportCases.open')}
                          </button>
                          <button 
                            onClick={() => handleFilterChange('case_status', 'IN_PROGRESS')}
                            className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white ${filters.case_status === 'IN_PROGRESS' ? 'bg-gray-100 dark:bg-gray-600 font-medium' : ''}`}
                          >
                            {t('customerSupportCases.inProgress')}
                          </button>
                          <button 
                            onClick={() => handleFilterChange('case_status', 'RESOLVED')}
                            className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white ${filters.case_status === 'RESOLVED' ? 'bg-gray-100 dark:bg-gray-600 font-medium' : ''}`}
                          >
                            {t('customerSupportCases.resolved')}
                          </button>
                          <button 
                            onClick={() => handleFilterChange('case_status', 'CLOSED')}
                            className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white ${filters.case_status === 'CLOSED' ? 'bg-gray-100 dark:bg-gray-600 font-medium' : ''}`}
                          >
                            {t('customerSupportCases.closed')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center gap-2 relative">
                      {t('customerSupportCases.creationDate')}
                      <button 
                        onClick={() => toggleFilter('created_at')}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        title={t('supportCase.filterDate')}
                        data-filter="created_at"
                      >
                        <FaFilterIcon size={12} className={filters.created_at ? "text-blue-500" : ""} />
                      </button>
                      <div 
                        id="filter-created_at" 
                        className="absolute mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-md p-2 z-20 border border-gray-200 dark:border-gray-600" 
                        style={{display: 'none'}}
                      >
                        <input
                          type="text"
                          placeholder={t('supportCase.filterDatePlaceholder')}
                          value={filters.created_at}
                          onChange={(e) => handleFilterChange('created_at', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center gap-2 relative">
                      {t('customerSupportCases.assignedTo')}
                      <button 
                        onClick={() => toggleFilter('assigned_to_name')}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        title={t('supportCase.filterAssignedTo')}
                        data-filter="assigned_to_name"
                      >
                        <FaFilterIcon size={12} className={filters.assigned_to_name ? "text-blue-500" : ""} />
                      </button>
                      <div 
                        id="filter-assigned_to_name" 
                        className="absolute mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-md p-2 z-20 border border-gray-200 dark:border-gray-600" 
                        style={{display: 'none'}}
                      >
                        <input
                          type="text"
                          placeholder={t('supportCase.filterAssignedToPlaceholder')}
                          value={filters.assigned_to_name}
                          onChange={(e) => handleFilterChange('assigned_to_name', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {isLoading ? (
                  <tr>
                    <td colSpan="5" className="py-4 px-4 text-center text-gray-500 dark:text-gray-400 text-sm">{t('customerSupportCases.loadingTickets')}</td>
                  </tr>
          ) : error ? (
                  <tr>
                    <td colSpan="5" className="py-4 px-4 text-center text-red-500 dark:text-red-400 text-sm">{error}</td>
                  </tr>
                ) : currentCases.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-4 px-4 text-center text-gray-500 dark:text-gray-400 text-sm rounded-bl-lg rounded-br-lg">{t('customerSupportCases.noTicketsFound')}</td>
                  </tr>
                ) : (
                  currentCases.map((caseItem, index) => {
                    const isLast = index === currentCases.length - 1 && totalPages <= 1;
                    return (
                      <tr key={caseItem.id} onClick={() => handleRowClick(caseItem)} className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer`}>
                        {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                        <td className={`px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>{caseItem.case_id}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 relative group">
                          <span>
                            {caseItem.subject.length > 30 
                              ? caseItem.subject.substring(0, 30) + '...' 
                              : caseItem.subject}
                          </span>
                          {caseItem.subject.length > 30 && (
                            <div className="absolute z-10 invisible group-hover:visible bg-gray-800 dark:bg-gray-600 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 min-w-max max-w-xs whitespace-normal break-words shadow-lg">
                              {caseItem.subject}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusChip(caseItem.case_status)}`}>
                            {caseItem.case_status}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{new Date(caseItem.created_at).toLocaleDateString()}</td>
                        {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                        <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 ${isLast ? 'rounded-br-lg' : ''}`}>{caseItem.assigned_to_name || t('customerSupportCases.notAssigned')}</td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
          </div>

          {/* Mobile Card View - 2 Columns */}
          <div className="lg:hidden p-4 space-y-4">
            {isLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">{t('customerSupportCases.loadingTickets')}</div>
            ) : error ? (
              <div className="text-center py-8 text-red-500 dark:text-red-400 text-sm">{error}</div>
            ) : currentCases.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">{t('customerSupportCases.noTicketsFound')}</div>
            ) : (
              currentCases.map((caseItem) => (
                <div key={caseItem.id} onClick={() => handleRowClick(caseItem)} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  {/* Case ID & Status */}
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCases.caseId')}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseItem.case_id}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCases.status')}</div>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusChip(caseItem.case_status)}`}>
                        {caseItem.case_status}
                      </span>
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="mb-3">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCases.subject')}</div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {caseItem.subject.length > 50 
                        ? caseItem.subject.substring(0, 50) + '...' 
                        : caseItem.subject}
                    </div>
                  </div>

                  {/* Creation Date & Assigned To */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCases.creationDate')}</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">{new Date(caseItem.created_at).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCases.assignedTo')}</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">{caseItem.assigned_to_name || t('customerSupportCases.notAssigned')}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {t('customerSupportCases.showing')}{' '}
                    <span className="font-medium">{indexOfFirstCase + 1}</span>
                    {' '}{t('customerSupportCases.to')}{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastCase, filteredCases.length)}
                    </span>{' '}
                    {t('customerSupportCases.of')} {t('customerSupportCases.total')}{' '}
                    <span className="font-medium">{filteredCases.length}</span>{' '}
                    {t('customerSupportCases.ticket')}
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('customerSupportCases.previous')}
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          currentPage === index + 1
                            ? 'bg-blue-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                        currentPage === totalPages
                          ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('customerSupportCases.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Customer Create Support Case Modal */}
      {isCreateModalOpen && (
        <CustomerCreateSupportCaseModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateCase}
        />
      )}

      {/* Filter Applied Popup */}
      {showFilterPopup && (
        <div className="fixed top-4 right-4 bg-blue-500 dark:bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-full p-1">
            <FaFilter className="text-blue-500 dark:text-blue-400" size={16} />
          </div>
          <div className="flex flex-col">
            <span className="font-medium">{t('customerSupportCases.filterApplied')}</span>
            <span className="text-sm text-blue-100 dark:text-blue-200">{filterPopupMessage}</span>
          </div>
          <button 
            onClick={() => setShowFilterPopup(false)}
            className="ml-2 text-blue-200 dark:text-blue-300 hover:text-white"
          >
            <MdClose size={18} />
          </button>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerSupportCasesPage;